import { Injectable } from '@angular/core';
import { parseEvents } from '@emma-helpers/default-events.helper';
import { getEventId } from '@emma-pages/manage/events/events.helper';
import {
  ApiChangeResponse,
  ApiManyResponse,
  ApiResponse,
  ApiResponsePagination,
  ApiSerialData,
  ApiSerialResponse,
} from 'emma-common-ts';
import {
  ApiRequest,
  AppEvent,
  AppEventPartial,
  AppEventsResponse,
  DateEventsResponse,
  EVENT_CATEGORY,
  EVENT_REPORT_RELATIVIZED_KPI,
  EventAsScreen,
  EventAttributeValuesTableRow,
  EventsReportGraph,
  EventsReportTableRow,
} from 'emma-common-ts/emma';
import { sortBy } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { EmmaApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class EventsService {
  constructor(private service: EmmaApiService) {}

  getEvents(type: 'all' | 'appevents' | 'default' | 'custom'): Observable<AppEventsResponse> {
    // ! 'screen' NO cumple esta definición, por eso no sale
    return this.service.get<AppEventsResponse>(`/event/list/${type}`).pipe(
      map((res) => {
        if (res?.data) {
          Object.assign(res, { data: parseEvents(sortBy(res.data, 'order')) });
        }
        return res;
      })
    );
  }
  getEvent(id: number): Observable<ApiResponse<AppEvent>> {
    return this.service.get<ApiResponse<AppEvent>>(`/event/get/event`, { id });
  }
  getScreenEvent(id: string): Observable<ApiResponse<EventAsScreen>> {
    return this.service.get<ApiResponse<EventAsScreen>>(`/event/get/screen`, { id });
  }
  exploreEvents(eventFilter: any): Observable<DateEventsResponse> {
    return this.service.get<DateEventsResponse>('/explore/events', eventFilter);
  }
  // Handy helpers
  getAppEvents = (): Observable<AppEventsResponse> => this.getEvents('appevents');
  getDefaultEvents = (): Observable<AppEventsResponse> => this.getEvents('default');
  getCustomEvents = (): Observable<AppEventsResponse> => this.getEvents('custom');
  getAllEvents = (): Observable<AppEventsResponse> => this.getEvents('all');
  // Las que se usan para rules
  getScreens(): Observable<ApiResponse<EventAsScreen[]>> {
    return this.service.get<ApiResponse<EventAsScreen[]>>(`/event/screens`);
  }
  // Tags de eventos
  getAllEventsTags(): Observable<ApiResponse<string[]>> {
    return this.service.get<ApiResponse<string[]>>(`/event/tags`);
  }

  saveEditEvent(event: AppEventPartial, eventCategory: EVENT_CATEGORY): Observable<ApiChangeResponse> {
    const endpoint = `/event/${eventCategory}`;
    const id = getEventId(event);
    if (id) {
      return this.service.post(endpoint, {
        ...event,
        id,
      });
    } else {
      return this.service.put(endpoint, {
        ...event,
      });
    }
  }

  deleteEvent(event: AppEventPartial): Observable<ApiChangeResponse> {
    const id = getEventId(event);
    return this.service.delete(`/event/${id}`);
  }

  getUserEventHistogram(eventId: number, customerId: string) {
    return this.service.get<ApiManyResponse<ApiSerialData<'label' | 'value'>>>(
      `/event/${eventId}/histogram-customer/${customerId}`
    );
  }

  getUserEventHistogramByEmmaId(eventId: number, emmaId: number) {
    return this.service.get<ApiManyResponse<ApiSerialData<'label' | 'value'>>>(
      `/event/${eventId}/histogram-emma-id/${emmaId}`
    );
  }

 /* start data by behavior events **/
  getDailyTotalEventsChart(eventId: string, params: ApiRequest<never> = {}) {
    const endpoint = `/report/events/${eventId}/graph`;
    return this.service.getRangeSerial<ApiSerialResponse<EventsReportGraph>>(endpoint, null, params);
  }

  getEventsTableData(params: ApiRequest<never> = {}) {
    return this.service.getRangeSerial<
      ApiResponse<{ data: EventsReportTableRow[]; pagination: ApiResponsePagination }>
    >(`/report/events`, null, params);
  }

  getFilterOptions() {
    return this.service.get<ApiResponse<{ [key: string]: string[] }>>(`/event/filter-options`);
  }

  putActivatedEvents(): Observable<ApiChangeResponse> {
    return this.service.put<ApiChangeResponse>('/app/prop', { eventsAnalysis: true });
  }

  getEventReportKpi(eventId: string, kpi: EVENT_REPORT_RELATIVIZED_KPI, params: ApiRequest<never> = {}) {
    const endpoint = `/report/events/${eventId}/relativized/${kpi}`;
    return this.service.getRangeSerial<ApiResponse<{ total: number; variation: number }>>(
      endpoint,
      null,
      params
    );
  }

  getAttributesFilterOptions(eventId: string, params: ApiRequest<never> = {}) {
    return this.service.getRangeSerial<ApiManyResponse<string>>(
      `/event/${eventId}/attributes/filter-options`,
      null,
      params
    );
  }

  getReportAttributesValues(eventId: string, attribute: string, params: ApiRequest<never> = {}) {
    const endpoint = `/report/events/${eventId}/attribute-values/${attribute}`; // If attribute is not provided, use '-'
    return this.service.getRangeSerial<
      ApiResponse<{ data: EventAttributeValuesTableRow[]; pagination: ApiResponsePagination }>
    >(endpoint, null, params);
  }
  /* end data by behavior events **/
}
